import { useCallback, useState } from 'react';
import { Stack, Text, DefaultButton, Modal, Spinner, SpinnerSize, IconButton, PrimaryButton } from '@fluentui/react';
import { IntegrationUpdateRequest, MicrosoftEntraIdIntegration, ProjectumProblemDetails } from '../../../../../../api/generated/data-contracts';
import { useControlledState } from '../../../../../../hooks/useControlledState';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../api/api';
import { MicrosoftEntraIdLogo } from '../../components/Logos/MicrosoftEntraIdLogo';
import { MicrosoftEntraIdLayout } from './MicrosoftEntraIdLayout';
import { SaveAndCloseButton } from '../SaveAndCloseButton';
import { ErrorModal } from '../ErrorModal';

export const EditMicrosoftEntraIdIntegration = ({
    integration: integrationProps,
    close,
    editIntegrations,
    setEditableIntegration,
}: {
    integration: MicrosoftEntraIdIntegration;
    close: () => void;
    editIntegrations: (updater: (data: MicrosoftEntraIdIntegration[]) => MicrosoftEntraIdIntegration[]) => void;
    setEditableIntegration: React.Dispatch<React.SetStateAction<MicrosoftEntraIdIntegration>>;
}) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [integration, setIntegration] = useControlledState(() => integrationProps, [integrationProps]);
    const { configuration, id, name, subscribedEvents } = integration;
    const [closePressed, setClosePressed] = useState(false);
    const [isConfirmSyncModalOpen, setIsConfirmSyncModalOpen] = useState(false);

    const hasChanged = integration !== integrationProps;

    const {
        mutateAsync: saveIntegration,
        isLoading,
        error: updateIntegrationError,
        reset: resetUpdateError,
    } = useMutation<MicrosoftEntraIdIntegration, ProjectumProblemDetails>({
        mutationFn: async () => {
            const update: IntegrationUpdateRequest = {
                name,
                configuration,
                id,
            };
            const updatedIntegration = integration;
            return ApiCalls.integrationEventUpdate(update).then(() => updatedIntegration);
        },
        onSuccess: integration => {
            editIntegrations(integrations =>
                integrations.map(existingIntegration => {
                    if (existingIntegration.id === integration.id) {
                        return integration;
                    }
                    return existingIntegration;
                }),
            );
            setEditableIntegration(integration);
        },
    });

    const {
        mutate: deleteIntegration,
        isLoading: isDeleting,
        error: deleteError,
        reset: resetDeleteError,
    } = useMutation({
        mutationFn: () => ApiCalls.integrationEventDelete(id),
        onSuccess: () => {
            editIntegrations(integrations => integrations.filter(integration => integration.id !== id));
            close();
        },
    });

    const handleClose = useCallback(() => {
        if (hasChanged) {
            setClosePressed(true);
        } else {
            close();
        }
    }, [close, hasChanged]);

    const {
        mutateAsync: runFullSync,
        isLoading: isRunningFullSync,
        error: runFullSyncError,
        reset: resetRunFullSyncError,
    } = useMutation({
        mutationFn: () => ApiCalls.integrationAadSyncusersCreate(),
        onSuccess: () => {
            setIsConfirmSyncModalOpen(false);
        },
    });

    return (
        <>
            <Stack horizontal horizontalAlign="center" verticalAlign="center" styles={{ root: { gap: 10, paddingTop: 20, paddingBottom: 20 } }}>
                {isLoading && <Spinner size={SpinnerSize.large} />}
                <MicrosoftEntraIdLogo height={'30px'} width={'30px'} />
                <Text variant="xxLarge">
                    {name}
                    {hasChanged && '*'}
                </Text>
                <IconButton iconProps={{ iconName: 'Cancel' }} styles={{ root: { position: 'absolute', right: 0, top: 0 } }} onClick={handleClose} />
            </Stack>
            <MicrosoftEntraIdLayout integration={integration} setIntegration={setIntegration} isLoading={isLoading} />
            <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 20px 20px', childrenGap: 10 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <DefaultButton disabled={isLoading} onClick={() => setConfirmDelete(true)}>
                        Delete
                    </DefaultButton>
                    <DefaultButton disabled={!hasChanged} onClick={() => setIntegration(integrationProps)}>
                        Revert changes
                    </DefaultButton>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton onClick={() => setIsConfirmSyncModalOpen(true)}>Run</PrimaryButton>
                    <SaveAndCloseButton disabled={!hasChanged} close={close} save={saveIntegration} />
                </Stack>
            </Stack>
            <Modal isOpen={isConfirmSyncModalOpen}>
                {isRunningFullSync ? (
                    <Stack horizontalAlign="center" styles={{ root: { height: 250, width: 400, padding: 30 } }} tokens={{ childrenGap: 50 }}>
                        <Text variant="xLarge" styles={{ root: { textAlign: 'center' } }}>
                            Entra ID User Sync
                        </Text>
                        <Spinner size={SpinnerSize.large} />
                        <Text variant="large" styles={{ root: { textAlign: 'center' } }}>
                            User synchronization is in progress.
                        </Text>
                    </Stack>
                ) : (
                    <Stack horizontalAlign="center" styles={{ root: { height: 250, width: 500, padding: 10 } }} tokens={{ childrenGap: 30 }}>
                        <Text variant="xLarge" styles={{ root: { textAlign: 'center' } }}>
                            Confirmation
                        </Text>
                        <Text variant="large" styles={{ root: { textAlign: 'center' } }}>
                            Are you sure you want to start full sync of users from the specified Microsoft Entra group to Time for Teams?
                        </Text>

                        <Stack horizontal horizontalAlign="end" tokens={{ padding: '20px', childrenGap: 10 }}>
                            <DefaultButton onClick={() => setIsConfirmSyncModalOpen(false)}>Cancel</DefaultButton>

                            <PrimaryButton onClick={() => runFullSync()} disabled={isRunningFullSync}>
                                Yes
                            </PrimaryButton>
                        </Stack>
                    </Stack>
                )}
            </Modal>
            <Modal isOpen={closePressed} onDismiss={() => setClosePressed(false)}>
                <Stack verticalAlign="space-between" styles={{ root: { height: 200, padding: 10 } }}>
                    <Text variant="xLarge">You currently have changes to "{name}"</Text>
                    <Stack horizontal horizontalAlign="end" tokens={{ padding: '50px 0 0 0', childrenGap: 10 }}>
                        <DefaultButton onClick={() => setClosePressed(false)}>Cancel</DefaultButton>
                        <PrimaryButton onClick={close} disabled={isDeleting}>
                            Discard changes
                        </PrimaryButton>
                    </Stack>
                </Stack>
            </Modal>
            <Modal isOpen={confirmDelete} {...(!isDeleting && { onDismiss: () => setConfirmDelete(false) })}>
                <Stack verticalAlign="space-between" styles={{ root: { height: 200, padding: 10 } }}>
                    <Text variant="xLarge">Are you sure you want to delete "{name}"?</Text>
                    {isDeleting && <Spinner size={SpinnerSize.large} label={`Deleting "${name}"....`} />}
                    <Stack horizontal horizontalAlign="end" tokens={{ padding: '50px 0 0 0', childrenGap: 10 }}>
                        <DefaultButton
                            styles={{
                                root: { backgroundColor: 'rgba(255, 0, 0, 1)', color: 'white' },
                                rootHovered: { backgroundColor: 'rgba(255, 0, 0, .5)' },
                                rootPressed: { backgroundColor: 'rgba(200, 0, 0, .5)' },
                            }}
                            onClick={() => deleteIntegration()}
                            disabled={isDeleting}
                        >
                            Yes
                        </DefaultButton>
                        <DefaultButton onClick={() => setConfirmDelete(false)} disabled={isDeleting}>
                            Cancel
                        </DefaultButton>
                    </Stack>
                </Stack>
            </Modal>
            <ErrorModal reset={resetUpdateError} error={updateIntegrationError} />
            <ErrorModal reset={resetDeleteError} error={deleteError} />
        </>
    );
};
